import { useMemo } from 'react';

import { logCustomEvent } from '@/shared/configs/firebase';
import { buildStoreLinks, resolveStoreCampaignName, type StorePlacement } from '@/shared/consts/app-links';
import { readLandingCampaign } from '@/shared/lib/store-attribution';
import { cn } from '@/shared/lib/utils/tw';

/**
 * TRAD-3711 — App Store · Google Play 버튼 한 벌. ~~/about·/support 가 같은 마크업을 각자 들고 있었다~~ → 여기 하나.
 * 모양은 /about 의 기존 버튼 그대로(bg-foreground · rounded-lg · font-medium) — 새 디자인 없음(UI 룰 1).
 * `size="sm"` 은 푸터용 — 색·모서리·굵기는 같고 글자·좌우 여백만 줄인다. 세로는 푸터 규칙대로 44(TRAD-3252).
 *
 * 링크 = 채널 추적 링크(app-links.ts): 첫 진입 출처(sessionStorage)가 있으면 출처 이름, 없으면 `placement`.
 * TRAD-3867(측정, marketer 발견 10/29) — 클릭마다 GA `store_click`(platform: ios|android · placement: 버튼 자리 · campaign: 실제 링크에 실린 이름).
 *   스토어 쪽 설치 수(ct/utm)와 우리 쪽 클릭 수를 같은 이름으로 맞춰 «클릭 → 설치» 전환을 볼 수 있게.
 * 버튼 라벨 «App Store»·«Google Play» 는 스토어 상표명이라 번역하지 않는다(종전과 같음).
 */
interface StoreButtonsProps {
  placement: StorePlacement;
  size?: 'md' | 'sm';
  className?: string;
}

const SIZE_CLASS = {
  md: 'px-6 py-3 text-sm',
  // 푸터 링크 터치 타깃 세로 44(TRAD-3252 — 푸터의 모든 <a> 에 min-h-11 + inline-flex items-center, 가드 테스트가 강제).
  sm: 'inline-flex items-center min-h-11 px-4 text-xs',
} as const;

export const StoreButtons = ({ placement, size = 'md', className }: StoreButtonsProps) => {
  const campaign = useMemo(() => resolveStoreCampaignName(placement, readLandingCampaign()), [placement]);
  const links = useMemo(() => buildStoreLinks(campaign), [campaign]);
  const onStoreClick = (platform: 'ios' | 'android') => () =>
    logCustomEvent('store_click', { platform, placement, campaign });
  const buttonClass = cn(
    'rounded-lg font-medium transition-colors bg-foreground text-background hover:opacity-90',
    SIZE_CLASS[size],
  );
  return (
    <div className={cn('flex items-center justify-center gap-4', size === 'sm' && 'gap-2', className)}>
      <a href={links.ios} target="_blank" rel="noopener noreferrer" className={buttonClass} onClick={onStoreClick('ios')}>
        App Store
      </a>
      <a
        href={links.android}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClass}
        onClick={onStoreClick('android')}
      >
        Google Play
      </a>
    </div>
  );
};
